import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, ProgressBar } from 'react-bootstrap';
import styles from './ActivityFeed.module.css';
import API from '../api';

const ACHIEVEMENT_TARGET = 9;

function fetchJson(url) {
    return fetch(url, { method: 'GET', credentials: 'include' })
        .then(r => r.ok ? r.json() : Promise.reject(new Error(`${url}: ${r.status}`)));
}

function formatDate(timestamp) {
    const d = new Date(timestamp);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString();
}

function ActivityFeed() {
    const [projectActivities, setProjectActivities] = useState([]);
    const [exploitActivities, setExploitActivities] = useState([]);
    const [trainingActivities, setTrainingActivities] = useState([]);
    const [systemNotifications, setSystemNotifications] = useState([]);
    const [achievementProgress, setAchievementProgress] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.allSettled([
            fetchJson(API.PROJECTS),
            fetchJson(API.NOTIFICATIONS),
            fetchJson(API.EXPLOITS),
            fetchJson(API.TRAINING),
        ]).then(([projectsRes, notificationsRes, exploitsRes, trainingRes]) => {
            if (projectsRes.status === 'fulfilled' && projectsRes.value?.status === 'success') {
                const projects = projectsRes.value.projects || [];
                setProjectActivities(projects.map(p => ({
                    title: p.title,
                    updated_at: p.updated_at,
                    action: p.created_at === p.updated_at ? 'created' : 'updated',
                    username: p.createdBy?.name || 'unknown',
                })));
                const completed = projects.filter(p => p.tag === 'Finished').length;
                setAchievementProgress(Math.min(Math.round((completed / ACHIEVEMENT_TARGET) * 100), 100));
            }

            if (notificationsRes.status === 'fulfilled') {
                setSystemNotifications(notificationsRes.value?.notifications || []);
            }

            if (exploitsRes.status === 'fulfilled' && exploitsRes.value?.status === 'success') {
                setExploitActivities(exploitsRes.value.exploits || []);
            }

            if (trainingRes.status === 'fulfilled' && trainingRes.value?.status === 'success') {
                const challenges = trainingRes.value.challenges || [];
                const activities = [];
                challenges.forEach(challenge => {
                    activities.push({
                        type: 'creation',
                        title: challenge.title,
                        category: challenge.category,
                        difficulty: challenge.difficulty,
                        username: challenge.created_by,
                        timestamp: challenge.created_at,
                    });
                    (challenge.completed_by || []).forEach(completion => {
                        activities.push({
                            type: 'completion',
                            title: challenge.title,
                            category: challenge.category,
                            difficulty: challenge.difficulty,
                            username: completion.username,
                            timestamp: completion.completed_at,
                        });
                    });
                });
                activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
                setTrainingActivities(activities);
            }

            setLoading(false);
        });
    }, []);

    if (loading) return <div className="loading-spinner"></div>;

    return (
        <Container fluid className={styles.activityFeedContainer}>
            <Row>
                <Col>
                    <h2 className={styles.sectionTitle}>System Notifications</h2>
                    <Card className={styles.card}>
                        <Card.Body className={styles.cardBody}>
                            {systemNotifications.length > 0 ? (
                                systemNotifications.slice(0, 5).map((notification, index) => (
                                    <div key={index} className={styles.notificationItem}>
                                        <div className={styles.notificationMessage}>
                                            {notification.message}
                                        </div>
                                        <div className={styles.notificationTimestamp}>
                                            {formatDate(notification.timestamp)}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className={styles.emptyText}>No new system notifications.</p>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col>
                    <h2 className={styles.sectionTitle}>Achievements</h2>
                    <Card className={styles.card}>
                        <Card.Body className={styles.cardBody}>
                            <h4 className={styles.achievementTitle}>
                                Complete {ACHIEVEMENT_TARGET} Projects
                            </h4>
                            <ProgressBar
                                now={achievementProgress}
                                label={`${achievementProgress}%`}
                                className={styles.progressBar}
                            />
                            <p className={styles.progressLabel}>Progress: {achievementProgress}%</p>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col>
                    <h2 className={styles.sectionTitle}>Project Activities</h2>
                    <Card className={styles.card}>
                        <Card.Body className={styles.cardBody}>
                            {projectActivities.length > 0 ? (
                                <ul className={styles.list}>
                                    {projectActivities.map((activity, index) => (
                                        <li key={index} className={styles.listItem}>
                                            <strong>{activity.username}</strong> {activity.action}{' '}
                                            <strong>{activity.title}</strong>{' '}
                                            <span className={styles.listMeta}>
                                                on {formatDate(activity.updated_at)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className={styles.emptyText}>No recent project activities.</p>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col>
                    <h2 className={styles.sectionTitle}>Exploit Activities</h2>
                    <Card className={styles.card}>
                        <Card.Body className={styles.cardBody}>
                            {exploitActivities.length > 0 ? (
                                <ul className={styles.list}>
                                    {exploitActivities.map((exploit, index) => (
                                        <li key={index} className={styles.listItem}>
                                            <strong>{exploit.title}</strong>: {exploit.vulnerability} affecting {exploit.affectedSystems}.{' '}
                                            <span className={styles.listMeta}>Status: {exploit.status}</span>
                                            {exploit.tags && (
                                                <div className={styles.listMeta}>Tags: {exploit.tags}</div>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className={styles.emptyText}>No recent exploit activities.</p>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col>
                    <h2 className={styles.sectionTitle}>Training Centre Activities</h2>
                    <Card className={styles.card}>
                        <Card.Body className={styles.cardBody}>
                            {trainingActivities.length > 0 ? (
                                <ul className={styles.list}>
                                    {trainingActivities.slice(0, 5).map((activity, index) => (
                                        <li key={index} className={styles.listItem}>
                                            <strong>{activity.username}</strong>
                                            {activity.type === 'creation' ? (
                                                <> created a new {activity.difficulty} challenge </>
                                            ) : (
                                                <> completed the {activity.difficulty} challenge </>
                                            )}
                                            <strong>{activity.title}</strong> ({activity.category})
                                            <div className={styles.listMeta}>
                                                {formatDate(activity.timestamp)}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className={styles.emptyText}>No recent training activities.</p>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

export default ActivityFeed;
